// DocumentDetails.tsx - Document management modal with preview, replace, download, and delete functionality
import React, { useState, useEffect } from 'react';
import { 
  X, Eye, Download, Trash2, RefreshCw, File, Image, FileText, 
  AlertCircle, CheckCircle, Clock, XCircle, Upload, Loader, Plus
} from 'lucide-react';
import { Document } from '../../types';
import LoadingSpinner from '../common/LoadingSpinner';

interface DocumentDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  onDocumentUpdate: () => void;
}

const DocumentDetails: React.FC<DocumentDetailsProps> = ({ isOpen, onClose, onDocumentUpdate }) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('date');

  useEffect(() => {
    if (isOpen) {
      fetchUserDocuments();
    }
  }, [isOpen]);

  const fetchUserDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const apiBase = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
        ? 'http://localhost:8000/api/'
        : (import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/');

      const response = await fetch(`${apiBase}documents/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const docs: Document[] = (data.documents || data || []).map((doc: any) => ({
          id: doc.id,
          name: doc.name || doc.document_name || 'Unknown',
          mimeType: doc.mimeType || doc.mime_type || 'application/octet-stream',
          ...doc,
          created_at: doc.created_at || new Date().toISOString()
        }));
        setDocuments(docs);
      }
    } catch (error: any) {
      console.error('Error fetching documents:', error);
      alert('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType?.startsWith('image/')) {
      return <Image className="w-5 h-5" />;
    } else if (mimeType === 'application/pdf') {
      return <FileText className="w-5 h-5" />;
    } else {
      return <File className="w-5 h-5" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'verified':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'verified':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date: Date | string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(typeof date === 'string' ? new Date(date) : date);
  };

  const handlePreviewDocument = (doc: Document) => {
    if (doc.fileData && (doc.mimeType?.startsWith('image/') || doc.mimeType === 'application/pdf')) {
      setPreviewDocument(doc);
      setShowPreview(true);
    } else {
      alert('Preview not available for this file type or file too large');
    }
  };

  const handleDownloadDocument = (doc: Document) => {
    if (doc.fileData) {
      const link = document.createElement('a');
      link.href = doc.fileData;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert('Download not available - file stored externally');
    }
  };

  const handleDeleteDocument = async (docId: number, docName: string) => {
    if (window.confirm(`Are you sure you want to delete "${docName}"? This action cannot be undone.`)) {
      try {
        const token = localStorage.getItem('authToken');
        const apiBase = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
          ? 'http://localhost:8000/api/'
          : (import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/');

        const response = await fetch(`${apiBase}documents/${docId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Failed to delete document');
        
        setDocuments(prev => prev.filter(doc => doc.id !== docId));
        
        if (onDocumentUpdate) {
          onDocumentUpdate();
        }
        
        alert('Document deleted successfully');
      } catch (error: any) {
        console.error('Error deleting document:', error);
        alert('Failed to delete document. Please try again.');
      }
    }
  };

  const fileToBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const validateFile = (file: File) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    
    if (file.size > maxSize) {
      alert('File size must be less than 5MB');
      return false;
    }
    
    if (!allowedTypes.includes(file.type)) {
      alert('Only PDF, JPG, and PNG files are allowed');
      return false;
    }
    
    return true;
  };

  const handleReplaceDocument = async (docId: number, _docType = 'general') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement)?.files?.[0];
      if (file && validateFile(file)) {
        try {
          setUploading(true);
          setUploadProgress(prev => ({ ...prev, [docId]: 0 }));
          setUploadProgress(prev => ({ ...prev, [docId]: 50 }));

          // TODO: Implement document replace via backend API
          console.warn('Document replace not yet implemented via API');
          
          setUploadProgress(prev => ({ ...prev, [docId]: 100 }));
          await fetchUserDocuments();

          if (onDocumentUpdate) {
            onDocumentUpdate();
          }

          alert('Document replaced successfully');
        } catch (error: any) {
          console.error('Error replacing document:', error);
          alert('Failed to replace document. Please try again.');
        } finally {
          setUploading(false);
          setTimeout(() => {
            setUploadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[docId];
              return newProgress;
            });
          }, 1000);
        }
      }
    };
    input.click();
  };

  const handleAddNewDocument = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement)?.files?.[0];
      if (file && validateFile(file)) {
        try {
          setUploading(true);
          const tempId = 'new-doc';
          setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));
          setUploadProgress(prev => ({ ...prev, [tempId]: 50 }));

          // TODO: Implement document upload via backend API
          console.warn('Document upload not yet implemented via API');

          setUploadProgress(prev => ({ ...prev, [tempId]: 100 }));
          await fetchUserDocuments();

          if (onDocumentUpdate) {
            onDocumentUpdate();
          }

          alert('Document uploaded successfully');
        } catch (error: any) {
          console.error('Error uploading document:', error);
          alert('Failed to upload document. Please try again.');
        } finally {
          setUploading(false);
          setTimeout(() => {
            setUploadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress['new-doc'];
              return newProgress;
            });
          }, 1000);
        }
      }
    };
    input.click();
  };

  const filteredAndSortedDocuments = documents
    .filter(doc => {
      if (filterStatus === 'all') return true;
      return (doc as any).status?.toLowerCase() === filterStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date((b as any).uploadedAt || b.created_at || 0).getTime() - new Date((a as any).uploadedAt || a.created_at || 0).getTime();
      } else if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'status') {
        return ((a as any).status || '').localeCompare((b as any).status || '');
      }
      return 0;
    });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-[min(36rem,calc(100vw-2rem))] sm:max-w-4xl max-h-[90vh] overflow-hidden my-auto">
        <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">My Documents</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Manage all your uploaded documents
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        
        <div className="p-6">
          {/* Filters and Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex flex-wrap gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-[color:var(--pmc-primary)] dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-[color:var(--pmc-primary)] dark:bg-gray-700 dark:text-white"
              >
                <option value="date">Sort by Date</option>
                <option value="name">Sort by Name</option>
                <option value="status">Sort by Status</option>
              </select>
            </div>
            
            {/* <button
              onClick={handleAddNewDocument}
              disabled={uploading}
              className="px-4 py-2 bg-[color:var(--pmc-primary)] text-white rounded-lg hover:bg-[color:var(--pmc-primary)] transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Add Document
            </button> */}
          </div>

          {/* Documents List */}
          <div className="space-y-4 max-h-[calc(90vh-300px)] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="medium" text="Loading documents..." />
              </div>
            ) : filteredAndSortedDocuments.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No documents found</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {filterStatus === 'all' 
                    ? "You haven't uploaded any documents yet." 
                    : `No documents with ${filterStatus} status.`
                  }
                </p>
              </div>
            ) : (
              filteredAndSortedDocuments.map(document => {
                const progress = uploadProgress[document.id || ''];
                
                return (
                  <div 
                    key={document.id}
                    className="border dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      {/* File Icon */}
                      <div className="flex-shrink-0 text-gray-500 dark:text-gray-400 mt-1">
                        {getFileIcon(document.mimeType)}
                      </div>
                      
                      {/* Document Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {document.name}
                            </h3>
                            <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                              <span>{formatFileSize((document as any).size || 0)}</span>
                              <span>{formatDate((document as any).uploadedAt || document.created_at)}</span>
                              {(document as any).petName && (
                                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded">
                                  {(document as any).petName}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Status Badge */}
                          <div className="flex items-center gap-2 ml-4">
                            {getStatusIcon((document as any).status)}
                            <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor((document as any).status)}`}>
                              {(document as any).status || 'Pending'}
                            </span>
                          </div>
                        </div>
                        
                        {/* Progress Bar (if uploading) */}
                        {progress !== undefined && (
                          <div className="mt-2">
                            <div className="flex items-center gap-2">
                              <Loader className="w-4 h-4 animate-spin text-blue-600" />
                              <span className="text-sm text-gray-600">Processing... {progress}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div 
                                className="bg-[color:var(--pmc-primary)] h-2 rounded-full transition-all duration-300"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* Document Notes/Review */}
                        {(document as any).notes && (
                          <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs">
                            <span className="font-medium text-yellow-800 dark:text-yellow-300">Note: </span>
                            <span className="text-yellow-700 dark:text-yellow-400">{(document as any).notes}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-1">
                        {/* Preview */}
                        {document.fileData && (document.mimeType?.startsWith('image/') || document.mimeType === 'application/pdf') && (
                          <button
                            onClick={() => handlePreviewDocument(document)}
                            className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Preview document"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        
                        {/* Download */}
                        {document.fileData && (
                          <button
                            onClick={() => handleDownloadDocument(document)}
                            className="p-2 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Download document"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        
                        {/* Replace */}
                        <button
                          onClick={() => handleReplaceDocument(document.id || 0, (document as any).type)}
                          disabled={uploading}
                          className="p-2 text-gray-500 hover:text-orange-600 dark:text-gray-400 dark:hover:text-orange-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                          title="Replace document"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        
                        {/* Delete */}
                        <button
                          onClick={() => handleDeleteDocument(document.id || 0, document.name)}
                          className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Delete document"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      {showPreview && previewDocument && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-[60] p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-[min(36rem,calc(100vw-2rem))] sm:max-w-4xl max-h-[90vh] overflow-hidden my-auto">
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {previewDocument.name}
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div className="p-4 max-h-[calc(90vh-100px)] overflow-auto">
              {previewDocument.mimeType?.startsWith('image/') ? (
                <img
                  src={previewDocument.fileData}
                  alt={previewDocument.name}
                  className="max-w-full h-auto mx-auto"
                />
              ) : previewDocument.mimeType === 'application/pdf' ? (
                <iframe
                  src={previewDocument.fileData}
                  className="w-full h-[600px] border-0"
                  title={previewDocument.name}
                />
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center">
                  Preview not available for this file type
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentDetails;